/**
 * DigestPrefToggle — small owner-only control letting an
 * institution choose between per-flag emails (`instant`, the noisy
 * default) and a single batched Monday-morning digest (`weekly`).
 *
 * Backed by POST /api/uni-portal/university/{id}/digest-pref.
 * When set to `weekly`, the per-flag notify hook inside
 * `data_gap_reports._notify_claim_owners` skips outbound sends.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Bell, Calendar, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function DigestPrefToggle({ universityId, initialValue }) {
  const [value, setValue] = useState(initialValue === "weekly" ? "weekly" : "instant");
  const [busy, setBusy] = useState(false);

  const set = async (next) => {
    if (next === value || busy) return;
    setBusy(true);
    try {
      await api.post(`/uni-portal/university/${universityId}/digest-pref`, {
        digest_pref: next,
      });
      setValue(next);
      toast.success(
        next === "weekly"
          ? "Switched to Monday-morning digest"
          : "Switched to instant alerts",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update preference");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="p-4 border border-stone-200 rounded-xl bg-white"
      data-testid="digest-pref-toggle"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-underground-sc tracking-widest text-stone-500">
          How you get alerted
        </span>
        {busy && <Loader2 className="h-3 w-3 animate-spin text-stone-400" />}
      </div>
      <div
        className="mt-2 inline-flex rounded-lg border border-stone-200 bg-stone-50 p-0.5"
        role="tablist"
      >
        <button
          role="tab"
          type="button"
          aria-selected={value === "instant"}
          onClick={() => set("instant")}
          disabled={busy}
          data-testid="digest-pref-instant-btn"
          className={
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition " +
            (value === "instant"
              ? "bg-white border border-stone-300 text-stone-900 shadow-sm"
              : "text-stone-500 hover:text-stone-800")
          }
        >
          <Bell className="h-3 w-3" /> Instant
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={value === "weekly"}
          onClick={() => set("weekly")}
          disabled={busy}
          data-testid="digest-pref-weekly-btn"
          className={
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition " +
            (value === "weekly"
              ? "bg-white border border-stone-300 text-stone-900 shadow-sm"
              : "text-stone-500 hover:text-stone-800")
          }
        >
          <Calendar className="h-3 w-3" /> Weekly digest
        </button>
      </div>
      <p className="mt-2 text-[11px] text-stone-500 leading-snug">
        {value === "instant"
          ? "You'll get an email every time a family flags a data field on your record. Best for small teams who prefer real-time triage."
          : "You'll get a single Monday-morning summary of all new flags and edits from the previous week. Best for teams who don't want their inbox interrupted."}
      </p>
    </div>
  );
}
